import React from "react";
import {IconTruck} from "../../../../../../components/buttons/IconButtons";
import {CustomTextField} from "../../../../../../components/CustomTextField";
import {isNotEmpty} from "../../../../../../Apis/functions";
import {THERMO_TRAILER_FIELD_LENGTH} from "../../../Model/Model";

/**
 * Fields every thermo trailer captures, in the order the form displays them.
 */
const THERMO_TRAILER_FIELDS = [
    {
        suffix: 'destination',
        name: 'destination',
        trans: 'destination',
        placeholder: 'enter_destination',
        className: 'col-sm-12 col-md-6'
    },
    {
        suffix: 'thermo_no',
        name: 'thermoNo',
        trans: 'thermo_no',
        placeholder: 'enter_unit_number',
        className: 'col-sm-12 col-md-6'
    },
    {
        suffix: 'seal_1_no',
        name: 'seal1No',
        trans: 'seal_1_no',
        placeholder: 'enter_seal_number',
        className: 'col-sm-12 col-md-6'
    },
    {
        suffix: 'seal_2_no',
        name: 'seal2No',
        trans: 'seal_2_no',
        placeholder: 'enter_seal_number',
        className: 'col-sm-12 col-md-6'
    },
    {
        suffix: 'aviso_czm',
        name: 'avisoCzmNo',
        trans: 'aviso_czm',
        placeholder: 'enter_aviso_czm_number',
        className: 'col-sm-12 col-md-6'
    },
    {
        suffix: 'dispatch_note_no',
        name: 'dispatchNoteNo',
        trans: 'dispatch_note_no',
        placeholder: 'enter_dispatch_note_number',
        className: 'col-sm-12 col-md-6'
    },
    {
        suffix: 'notes',
        name: 'notes',
        trans: 'notes',
        placeholder: 'add_any_relevant_notes_or_observations',
        className: 'col-12',
        multiline: true,
        marginBottom: false
    }
];

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @description
 * Renders the information of a thermo trailer of the poultry truck exit form.
 * Every trailer captures the same fields, so the section it writes in the form,
 * the title it displays and whether the requester has to complete it come from
 * its configuration.
 *
 * @param {Function} t - Translation function.
 * @param {Object} section - Configuration of the thermo trailer being captured.
 * @param {Object} materialRequest - Current form information.
 * @param {Function} handleChange - Change handler of the form.
 * @param {boolean} emptyFields - Indicates whether the required fields must be highlighted.
 * @param {boolean} required - Indicates whether the trailer has to be captured,
 * because the requester leaves it to the guard and the guard reads it at the gate.
 * @param {boolean} disabled - Indicates whether the whole trailer is only read.
 * @param {Object} [registered] - Trailer as the folio registered it; every value
 * it already carries is only read, so the guard completes just the blank ones.
 * @param {string} [contentClassName] - Spacing the fields are read with inside the card.
 */
const ThermoTrailerSection = ({
    t,
    section,
    materialRequest,
    handleChange,
    emptyFields,
    required = true,
    disabled = false,
    registered = null,
    contentClassName = 'row mt-3 mb-3'
}) => {

    const trailer = materialRequest?.[section.fieldName];
    // A trailer that is only read, or that is left to whoever captures it next,
    // asks for nothing, so its fields are neither required nor marked as missing
    const isRequired = required && section.required && !disabled;

    /**
     * Forwards the change of a field to the form handler.
     *
     * The fields of every trailer share their name, so the change is written in
     * the section of the form that the trailer being captured owns.
     *
     * @param {Object} e - Change event of the field.
     *
     * @returns {void}
     */
    const handleTrailerChange = (e) => {
        handleChange(e, '', section.fieldName);
    };

    return (
        <div className="border-full-shades-300 background-general-00 border-radius-8px mb-3 pb-3">
            <div className={"d-flex align-items-center p-2"}>
                <div className={"mr-2 d-flex align-items-center"}><IconTruck/></div>
                <p className={"fs-14px ibl-font-w-600"}>{t(section.title)}</p>
            </div>
            <div className={"border-bottom-shades-300"}/>

            <div className={contentClassName}>
                {THERMO_TRAILER_FIELDS.map(({suffix, name, trans, placeholder, className, multiline, marginBottom}) => {
                    // A value the folio was registered with is only read, so it
                    // is neither written again nor asked for
                    const isRegistered = isNotEmpty(registered?.[name]);
                    const isFieldRequired = isRequired && !isRegistered;

                    return (
                        <div className={className} key={name}>
                            <CustomTextField
                                id={`txt_${section.idPrefix}_${suffix}`}
                                length={THERMO_TRAILER_FIELD_LENGTH[name]}
                                name={name}
                                value={trailer?.[name]}
                                trans={trans}
                                type={'text'}
                                multiline={multiline}
                                onChange={handleTrailerChange}
                                emptyFields={emptyFields && isFieldRequired}
                                required={isFieldRequired}
                                disabled={disabled || isRegistered}
                                placeholder={t(placeholder)}
                                {...(marginBottom !== undefined && {marginBottom})}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ThermoTrailerSection;
